import React, {useEffect, useMemo, useState} from 'react';
import FormRequest from '../FormRequest';
import SubmitProblemDialog from '../../../Forms/FormBuilder/SubmitProblemDialog';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {
    useGetMyFormRequestQuery,
    useGetFormRequestQuery,
    useGetStudentNotificationFormRequestQuery,
    useSubmitFormMutation, useGetEmployeeNotificationFormRequestQuery,
    useEditFormResponseMutation,
    useGetResponseEditsQuery,
    useLazyCheckContactEmailForStudentRequestQuery,
    useLazyCheckContactEmailForStudentSelfServiceQuery
} from '../../../../../api/services/formRequestsService';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import useUser from '../../../../../utility/hooks/useUser';
import PropTypes from 'prop-types';
import objectToFormData from '../../../../../utility/objectToFormData';
import {generateUUID} from '../../../../../utility/uuidUtil';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import {downloadFormRequestAttachment} from '../../../../../api/services/filesService';
import {ROLES} from '../../../../../constants/roles';
import {Box, Checkbox, FormControlLabel, Paper, Typography} from '@mui/material';
import ResponseEditHistory from '../../../../../shared-components/ResponseEditHistory';

const getAssigneeDisplayName = (item = {}) => {
    const fullName = item.fullName || item.fullname || item.name;
    if (fullName) return fullName;

    const forename = item.forename || item.firstName || item.givenName || '';
    const surname = item.surname || item.lastName || item.familyName || '';
    const combined = `${forename} ${surname}`.trim();
    if (combined) return combined;

    return item.email || '';
};

const toAssigneeOption = (item = {}) => {
    const id = item.formRequestAssignmentId;
    if (id === undefined || id === null || id === '') return null;

    return {
        id: String(id),
        label: getAssigneeDisplayName(item),
        email: item.email || null
    };
};

const extractAssigneeOptions = (data) => {
    if (!data) return [];

    const rawCandidates = [
        data.assignees,
        data.assignments,
        data.formRequestAssignments,
        data.parentAssignments,
        data.parentContacts,
        data.contacts,
    ].find(Array.isArray) ?? [];

    const dedupedById = new Map();
    rawCandidates
        .map(toAssigneeOption)
        .filter(Boolean)
        .forEach((option) => {
            if (!dedupedById.has(option.id)) {
                dedupedById.set(option.id, option);
            }
        });

    return Array.from(dedupedById.values());
};

const extractAssigneeOptionsFromRouteState = (routeState) => {
    const assignees = routeState?.assignees;
    if (!Array.isArray(assignees)) return [];

    return assignees
        .filter(item => item?.id != null && item.id !== '')
        .map(item => ({
            id: String(item.id),
            label: item.label || '',
            email: item.email || null
        }));
};

const SubmitPrivateForm = ({
                               navigateBackRoute,
                               personalForm
                           }) => {
    const {user} = useUser();
    const location = useLocation();
    const isPersonalForm = typeof personalForm === 'function'
        ? personalForm({user})
        : personalForm;
    const {
        formRequestId,
        formId,
        studentId,
        id: employeeId
    } = useParams();
    const [linkedPersonParams] = useSearchParams();
    const linkedStudentId = linkedPersonParams.get('linkedStudentId');
    const linkedEmployeeId = linkedPersonParams.get('linkedEmployeeId');
    // AD-2695: 'Edit Response' opens the submitted response with ?edit=1.
    const isEditingResponse = !!formRequestId && linkedPersonParams.get('edit') === '1';
    const [shareWithCreator, setShareWithCreator] = useState(false);
    const isStudent = !!studentId;
    const isEmployee = !!employeeId || user.hasRole(ROLES.EMPLOYEE);
    const dispatch = useDispatch();
    const [checkContactEmailForStudentRequest] =
        useLazyCheckContactEmailForStudentRequestQuery();
    const [checkContactEmailForStudentSelfService] =
        useLazyCheckContactEmailForStudentSelfServiceQuery();
    const initialAssigneeOptionsFromRoute = useMemo(
        () => extractAssigneeOptionsFromRouteState(location.state ?? {}),
        [location.state]
    );
    const initialSelectedAssignmentIdFromRoute = location.state?.selectedAssignmentId != null
        ? String(location.state.selectedAssignmentId)
        : undefined;
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(initialSelectedAssignmentIdFromRoute);
    const getFormRequestQueryArg = selectedAssignmentId
        ? {
            requestId: formRequestId,
            assignmentId: selectedAssignmentId
        }
        : formRequestId;
    const {
        data: formRequestData,
        isLoading: isLoadingFormRequest,
        isFetching: isFetchingFormRequest,
        error: formRequestError,
        refetch: refetchFormRequest
    } = useGetFormRequestQuery(getFormRequestQueryArg, {
        skip: isPersonalForm || !formRequestId,
        refetchOnMountOrArgChange: true
    });
    const {
        data: myFormRequestData,
        isLoading: isLoadingMyFormRequestData,
        isFetching: isFetchingMyFormRequestData,
        error: myFormRequestError,
        refetch: refetchMyFormRequest
    } = useGetMyFormRequestQuery(formRequestId, {
        skip: !formRequestId || !isPersonalForm,
        refetchOnMountOrArgChange: true
    });
    const {
        data: studentData,
        isLoading: isLoadingStudent,
        isFetching: isFetchingStudent,
        error: errorStudent,
    } = useGetStudentNotificationFormRequestQuery(
        {
            formId,
            personId: studentId
        },
        {
            skip: !formId || !isStudent,
            refetchOnMountOrArgChange: true
        }
    );
    const {
        data: employeeData,
        isLoading: isLoadingEmployee,
        isFetching: isFetchingEmployee,
        error: errorEmployee,
    } = useGetEmployeeNotificationFormRequestQuery(
        {
            formId,
            personId: employeeId,
            linkedStudentId,
            linkedEmployeeId
        },
        {
            skip: !formId || !isEmployee,
            refetchOnMountOrArgChange: true
        }
    );
    const [submitForm, {
        isLoading: isFormSubmitLoading,
        isSuccess: isFormSubmitSuccess,
        isError: isFormSubmitError,
        error: submitError
    }] = useSubmitFormMutation();
    const [editFormResponse, {isLoading: isSavingEdit}] = useEditFormResponseMutation();
    const {data: responseEdits, refetch: refetchResponseEdits} = useGetResponseEditsQuery(formRequestId, {
        skip: !formRequestId,
        refetchOnMountOrArgChange: true
    });
    const downloadFile = useFileDownloader();
    const navigate = useNavigate();
    const resolvedNavigateBackRoute = location.state?.navigateBackRoute ?? navigateBackRoute;
    const generalFormData = studentData ?? employeeData;
    const isLoadingGeneralForm = isLoadingStudent || isLoadingEmployee;
    const isFetchingGeneralForm = isFetchingStudent || isFetchingEmployee;
    const generalFormError = generalFormData ? undefined : (errorStudent ?? errorEmployee);
    const isLoading = isLoadingFormRequest ||
        isLoadingGeneralForm ||
        isLoadingMyFormRequestData;
    const isFetching = isFetchingFormRequest ||
        isFetchingMyFormRequestData ||
        isFetchingGeneralForm;
    const error = formRequestError || generalFormError || myFormRequestError;
    const formRequest = formRequestData || generalFormData || myFormRequestData;
    const isElevated = user?.hasElevatedAccess?.() === true;
    const isParentRole = user?.hasRole?.(ROLES.PARENT) === true;
    const isParentView = !!isPersonalForm && !isElevated;
    const isSchoolAdmin = user?.hasRole?.(ROLES.SCHOOL_ADMIN) === true;
    const assigneeOptionsFromLoadedRequest = useMemo(
        () => extractAssigneeOptions(formRequestData),
        [formRequestData]
    );
    const assigneeOptions = useMemo(
        () => assigneeOptionsFromLoadedRequest.length > 0
            ? assigneeOptionsFromLoadedRequest
            : initialAssigneeOptionsFromRoute,
        [assigneeOptionsFromLoadedRequest, initialAssigneeOptionsFromRoute]
    );

    useEffect(() => {
        if (selectedAssignmentId) return;
        if (assigneeOptions.length === 0) return;

        setSelectedAssignmentId(assigneeOptions[0].id);
    }, [assigneeOptions, selectedAssignmentId]);

    useEffect(() => {
        if (isFormSubmitError || isFormSubmitSuccess) {
            if (isFormSubmitSuccess) {
                if (formRequestId) {
                    if (isPersonalForm) {
                        refetchMyFormRequest();
                    } else {
                        refetchFormRequest();
                    }
                } else {
                    navigate('submit-success', {replace: true});
                }

            }
            showNotification(isFormSubmitSuccess);
        }
    }, [isFormSubmitSuccess, isFormSubmitError, submitError]);

    const [refusal, setRefusal] = useState(null);
    const showNotification = success => {
        if (success === true) {
            dispatch(showSnackbar({
                message: `${formRequest.formName} submitted successfully`,
                severity: 'success'
            }));
        } else if (success === false) {
            // AD-2692: a refusal is shown in a pop-up that stays until it is closed.
            setRefusal(submitError?.message ?? submitError ?? 'This form could not be submitted. Please try again.');
        }
    };

    const handleDownloadAttachment = async (fileId) => {
        await downloadFile(downloadFormRequestAttachment, {
            fileId,
            formRequestId,
            roles: user.roles,
            studentId: formRequest.studentId,
            shouldDownloadByCurrentUser: isPersonalForm
        });
    };

    const handleSubmit = async (data, placeholders) => {
        const responses = {};
        const attachments = [];
        const attachmentKeys = [];

        for (const key in data) {
            if (data[key] instanceof Blob) {
                const file = data[key];
                const fileKey = generateUUID();

                attachments.push(file);
                attachmentKeys.push(fileKey);
                responses[key] = {
                    name: file.name,
                    fileKey
                };
            } else {
                responses[key] = data[key];
            }
        }

        if (isEditingResponse) {
            if (attachments.length > 0) {
                setRefusal('A new file cannot be uploaded when editing a response. The other answers can be changed.');
                return;
            }
            try {
                await editFormResponse({
                    formRequestId,
                    data: JSON.stringify(responses),
                    shareWithCreator: isSchoolAdmin && shareWithCreator
                }).unwrap();
            } catch (editError) {
                setRefusal(typeof editError === 'string' ? editError : 'Your changes could not be saved. Please try again.');
                return;
            }
            dispatch(showSnackbar({message: 'Your changes have been saved', severity: 'success'}));
            const params = new URLSearchParams(linkedPersonParams);
            params.delete('edit');
            const query = params.toString();
            navigate(`${location.pathname}${query ? `?${query}` : ''}`, {replace: true, state: location.state});
            if (isPersonalForm) {
                refetchMyFormRequest();
            } else {
                refetchFormRequest();
            }
            refetchResponseEdits();
            return;
        }

        await submitForm(objectToFormData({
            id: formRequestId,
            responses: JSON.stringify(responses),
            formId,
            studentId: studentId ?? linkedStudentId,
            employeeId: employeeId ?? linkedEmployeeId,
            linkedEmployeeId,
            attachments,
            attachmentKeys,
            placeholders
        }));
    };

    const onEmailCheck = async (email) => {
        const trimmed = (email ?? '').trim();
        if (!trimmed) return null;

        try {
            if (formRequestId) {
                return await checkContactEmailForStudentRequest({
                    requestId: Number(formRequestId),
                    email: trimmed
                }).unwrap();
            }

            if (formId && studentId) {
                return await checkContactEmailForStudentSelfService({
                    formId: Number(formId),
                    studentId: Number(studentId),
                    email: trimmed
                }).unwrap();
            }

            return null;
        } catch {
            return null;
        }
    };

    return <>
    <SubmitProblemDialog open={!!refusal} onClose={() => setRefusal(null)} serverMessage={refusal}/>
    <FormRequest
        generalForm={formId !== undefined && (studentId !== undefined || isEmployee)}
        formRequest={formRequest}
        // Needed by questions that belong to this particular request rather than to the form -
        // a Stripe Payment question reads its payment by it, both to take one and to refund one.
        formRequestId={formRequestId}
        isLoading={isLoading || isFetching}
        isSubmitting={isFormSubmitLoading || isSavingEdit}
        editMode={isEditingResponse}
        editModeHeader={isEditingResponse && isSchoolAdmin
            ? (
                <FormControlLabel
                    sx={{mt: 1}}
                    control={<Checkbox
                        checked={shareWithCreator}
                        onChange={(event) => setShareWithCreator(event.target.checked)}
                    />}
                    label="Share edits with the form creator"
                />
            )
            : null}
        error={error}
        onSubmit={handleSubmit}
        onDownloadAttachment={handleDownloadAttachment}
        navigateBackRoute={resolvedNavigateBackRoute}
        onEmailCheck={onEmailCheck}
        isParentView={isParentView}
        isElevated={isElevated}
        isParentRole={isParentRole}
        assigneeOptions={assigneeOptions}
        selectedAssigneeId={selectedAssignmentId}
        onAssigneeChange={setSelectedAssignmentId}
        formAudience={location.state?.formAudience}
    />
    {!isEditingResponse && responseEdits?.length > 0 && (
        <Box sx={{maxWidth: 1000, mx: 'auto', px: 2, pb: 4}}>
            <Paper variant="outlined" sx={{p: 2}}>
                <Typography variant="h6" sx={{mb: 1.5}}>Edit history</Typography>
                <ResponseEditHistory edits={responseEdits}/>
            </Paper>
        </Box>
    )}
    </>;
};

SubmitPrivateForm.defaultProps = {
    personalForm: false
}

SubmitPrivateForm.propTypes = {
    navigateBackRoute: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            pathname: PropTypes.string,
            state: PropTypes.object,
        }),
    ]).isRequired,
    personalForm: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
    ]),
};
export default SubmitPrivateForm;
